import { Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { AlertController } from '@ionic/angular';
import { ApiV2Service } from './api-v2.service';
import { ApiV1Service } from './api-v1.service';
import { ActivityTrackingService } from './activity-tracking.service';

@Injectable({
  providedIn: 'root'
})
export class InactivityService {

  private readonly TOTAL_MS = 3 * 60 * 60 * 1000; // 3 hours
  private readonly WARNING_COUNTDOWN_MS = 30 * 1000; // 30 seconds
  private readonly WARNING_AFTER_MS = this.TOTAL_MS - this.WARNING_COUNTDOWN_MS;
  /** Backend idle probe while tab may be open but JS timers unreliable. */
  private readonly SESSION_CHECK_MS = 10 * 60 * 1000; // 10 minutes
  private readonly STORAGE_KEY = 'ionic_inactivity_last';
  private readonly STAY_KEY = 'ionic_inactivity_stay';
  private readonly EXCLUDED_ROUTES = ['/auth', '/login', '/register', '/forgot-password'];
  /** Avoid re-arming 3h timers on every touchmove/scroll (mobile Chrome). */
  private readonly RESET_THROTTLE_MS = 1000;

  private warningTimer: any;
  private logoutTimer: any;
  private sessionCheckTimer: any;
  private currentAlert: HTMLIonAlertElement | null = null;
  private countdownInterval: any;
  private isInitialized = false;
  private warningActive = false;
  private loggingOut = false;
  private stayHandled = false;
  private lastResetAt = 0;

  constructor(
    private router: Router,
    private alertCtrl: AlertController,
    public apiV2Service: ApiV2Service,
    private injector: Injector
  ) {}

  private get activityTracking(): ActivityTrackingService | null {
    try {
      return this.injector.get(ActivityTrackingService, null);
    } catch {
      return null;
    }
  }

  private get apiV1(): ApiV1Service | null {
    try {
      return this.injector.get(ApiV1Service, null);
    } catch {
      return null;
    }
  }

  public initialize() {
    if (this.isInitialized) return;

    this.startActivityListeners();
    this.listenToOtherTabs();
    this.startMobileResumeListeners();

    this.router.events.subscribe(() => {
      if (this.isOnExcludedRoute()) {
        this.stopMonitoring();
      } else {
        this.resetTimers();
        this.startSessionHeartbeat();
      }
    });

    if (!this.isOnExcludedRoute()) {
      this.resetTimers();
      this.startSessionHeartbeat();
    }

    this.isInitialized = true;
  }

  public resetAfterLogin() {
    this.loggingOut = false;
    this.warningActive = false;
    this.stayHandled = false;
    this.lastResetAt = 0;
    localStorage.setItem(this.STORAGE_KEY, Date.now().toString());
    this.resetTimers();
    this.startSessionHeartbeat();
  }

  private isOnExcludedRoute(): boolean {
    const currentUrl = this.router.url;
    return this.EXCLUDED_ROUTES.some(route => currentUrl.startsWith(route));
  }

  private stopMonitoring() {
    clearTimeout(this.warningTimer);
    clearTimeout(this.logoutTimer);
    clearInterval(this.countdownInterval);
    this.clearSessionHeartbeat();
    this.warningActive = false;
    this.stayHandled = false;
    if (this.currentAlert) {
      this.currentAlert.dismiss().catch(() => {});
      this.currentAlert = null;
    }
  }

  private startActivityListeners() {
    const activityEvents: (keyof DocumentEventMap)[] = [
      'touchstart',
      'touchmove',
      'touchend',
      'touchcancel',
      'pointerdown',
      'pointermove',
      'mousedown',
      'mousemove',
      'mouseup',
      'click',
      'dblclick',
      'keydown',
      'keyup',
      'keypress',
      'scroll',
      'wheel',
      'input',
      'change',
      'focusin',
      'focusout',
    ];

    activityEvents.forEach(event => {
      document.addEventListener(event, () => this.userActive(), true);
    });
  }

  /**
   * Mobile Chrome (and iOS Safari) throttle/suspend long setTimeouts when the tab
   * is backgrounded or the screen is locked. Wall-clock checks on resume keep
   * the 3-hour rule correct.
   */
  private startMobileResumeListeners() {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        this.handleReturnToApp();
      }
    });

    window.addEventListener('pageshow', () => this.handleReturnToApp());
    window.addEventListener('focus', () => this.handleReturnToApp());

    if ((window as any).Capacitor || (window as any).cordova) {
      document.addEventListener('resume', () => this.handleReturnToApp(), false);
      document.addEventListener('deviceready', () => this.handleReturnToApp(), false);
    }
  }

  private handleReturnToApp() {
    if (this.isOnExcludedRoute() || this.loggingOut) return;

    const stored = localStorage.getItem(this.STORAGE_KEY);
    let last = Number(stored);
    if (!stored || !Number.isFinite(last) || last <= 0) {
      last = Date.now();
      try {
        localStorage.setItem(this.STORAGE_KEY, String(last));
      } catch {
        /* ignore */
      }
    }
    const elapsed = Date.now() - last;

    // Idle already exceeded 3 hours while tab/screen was asleep
    if (elapsed >= this.TOTAL_MS) {
      this.logout();
      return;
    }

    // Warning window: keep/show popup and re-arm logout from wall clock
    if (elapsed >= this.WARNING_AFTER_MS) {
      clearTimeout(this.warningTimer);
      clearTimeout(this.logoutTimer);
      const toLogout = this.TOTAL_MS - elapsed;
      this.logoutTimer = setTimeout(() => this.logout(), Math.max(0, toLogout));

      if (this.warningActive && this.currentAlert) {
        this.startWarningCountdown();
      } else {
        this.showAlert();
      }
      return;
    }

    // Still within normal idle window — re-arm timers from wall clock
    this.warningActive = false;
    this.resetTimers();
  }

  private listenToOtherTabs() {
    window.addEventListener('storage', (e) => {
      if (e.key === this.STORAGE_KEY && !this.warningActive) {
        this.resetTimers();
      }
      if (e.key === this.STAY_KEY) {
        this.extendSessionFromWarning();
      }
    });
  }

  private userActive() {
    if (this.isOnExcludedRoute() || this.loggingOut) {
      return;
    }
    // While warning popup is open, only "I'm still here" may extend the session
    if (this.warningActive) {
      return;
    }

    localStorage.setItem(this.STORAGE_KEY, Date.now().toString());

    const now = Date.now();
    if (now - this.lastResetAt < this.RESET_THROTTLE_MS) {
      return;
    }
    this.lastResetAt = now;
    this.resetTimers();
  }

  private resetTimers() {
    if (this.isOnExcludedRoute() || this.loggingOut) {
      this.stopMonitoring();
      return;
    }

    clearTimeout(this.warningTimer);
    clearTimeout(this.logoutTimer);
    clearInterval(this.countdownInterval);

    const stored = localStorage.getItem(this.STORAGE_KEY);
    let last = Number(stored);
    if (!stored || !Number.isFinite(last) || last <= 0) {
      last = Date.now();
      try {
        localStorage.setItem(this.STORAGE_KEY, String(last));
      } catch {
        /* ignore */
      }
    }
    const elapsed = Date.now() - last;

    if (elapsed >= this.TOTAL_MS) {
      this.logout();
      return;
    }

    const toWarning = this.WARNING_AFTER_MS - elapsed;
    const toLogout = this.TOTAL_MS - elapsed;

    if (!this.warningActive) {
      if (toWarning > 0) {
        this.warningTimer = setTimeout(() => this.showAlert(), toWarning);
      } else {
        this.showAlert();
      }
    }

    this.logoutTimer = setTimeout(() => this.logout(), toLogout);
  }

  private async showAlert() {
    if (this.currentAlert || this.loggingOut) {
      return;
    }
    // Allow re-entry when warningActive was set but alert failed
    this.warningActive = true;
    this.stayHandled = false;

    const last = Number(localStorage.getItem(this.STORAGE_KEY)) || Date.now();
    const elapsed = Date.now() - last;
    const remainingMs = Math.max(0, this.TOTAL_MS - elapsed);
    const seconds = Math.max(1, Math.ceil(remainingMs / 1000));

    this.playBeep();
    this.activityTracking?.trackSessionTimeoutWarning();

    const alert = await this.alertCtrl.create({
      header: 'Session Expiring',
      message: `You will be logged out in ${seconds} seconds due to inactivity.`,
      backdropDismiss: false,
      cssClass: 'inactivity-alert',
      buttons: [
        {
          text: "I'm still here",
          cssClass: 'alert-button-primary',
          handler: () => {
            this.extendSessionFromWarning();
            return false;
          }
        }
      ]
    });

    this.currentAlert = alert;
    await alert.present();
    this.startWarningCountdown();

    alert.onDidDismiss().then(() => {
      clearInterval(this.countdownInterval);
      this.currentAlert = null;
    });
  }

  private startWarningCountdown() {
    clearInterval(this.countdownInterval);

    this.countdownInterval = setInterval(() => {
      if (this.stayHandled || this.loggingOut) {
        clearInterval(this.countdownInterval);
        return;
      }

      const lastTick = Number(localStorage.getItem(this.STORAGE_KEY)) || Date.now();
      const elapsedTick = Date.now() - lastTick;
      const remainingTickMs = Math.max(0, this.TOTAL_MS - elapsedTick);
      const seconds = Math.max(0, Math.ceil(remainingTickMs / 1000));

      if (this.currentAlert) {
        this.currentAlert.message = `You will be logged out in ${seconds} seconds due to inactivity.`;
      }

      if (seconds > 0 && seconds % 5 === 0) {
        this.playBeep();
      }

      if (seconds <= 0) {
        clearInterval(this.countdownInterval);
        this.logout();
      }
    }, 1000);
  }

  /** Extend session when user clicks "I'm still here". */
  private extendSessionFromWarning() {
    if (this.stayHandled || this.loggingOut) {
      return;
    }
    this.stayHandled = true;
    this.warningActive = false;

    clearTimeout(this.warningTimer);
    clearTimeout(this.logoutTimer);
    clearInterval(this.countdownInterval);

    localStorage.setItem(this.STORAGE_KEY, Date.now().toString());
    localStorage.setItem(this.STAY_KEY, Date.now().toString());
    this.lastResetAt = 0;

    const alertRef = this.currentAlert;
    this.currentAlert = null;
    if (alertRef) {
      alertRef.dismiss().catch(() => {});
    }

    this.resetTimers();
  }

  private playBeep() {
    try {
      const AudioCtx = (window as any).AudioContext || (window as any).webkitAudioContext;
      if (!AudioCtx) {
        return;
      }
      const ctx = new AudioCtx();
      const oscillator = ctx.createOscillator();
      const gain = ctx.createGain();
      oscillator.type = 'sine';
      oscillator.frequency.value = 880;
      gain.gain.value = 0.15;
      oscillator.connect(gain);
      gain.connect(ctx.destination);
      oscillator.start();
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.35);
      oscillator.stop(ctx.currentTime + 0.35);
      oscillator.onended = () => {
        try {
          ctx.close();
        } catch {
          /* ignore */
        }
      };
    } catch {
      /* ignore — mobile browsers may block audio without a gesture */
    }
  }

  private async logout() {
    if (this.loggingOut) {
      return;
    }
    this.loggingOut = true;
    this.warningActive = false;
    this.stayHandled = false;

    clearTimeout(this.warningTimer);
    clearTimeout(this.logoutTimer);
    clearInterval(this.countdownInterval);

    const alertRef = this.currentAlert;
    this.currentAlert = null;
    if (alertRef) {
      try {
        await alertRef.dismiss();
      } catch {
        /* ignore */
      }
    }

    // Cap logout time at last activity + 3h (sleep/wake must not store "now" as 40h+ duration)
    const last =
      Number(localStorage.getItem(this.STORAGE_KEY)) || Date.now() - this.TOTAL_MS;
    const cappedMs = Math.min(Date.now(), last + this.TOTAL_MS);
    const logoutAtSql = this.formatSqlDateTime(cappedMs);

    localStorage.removeItem(this.STORAGE_KEY);
    localStorage.removeItem(this.STAY_KEY);

    this.clearSessionHeartbeat();
    this.playBeep();

    // Session expired after 3 hours idle → save logout_at + logout_type = IDLE_3HR
    await this.activityTracking?.trackLogout('IDLE_3HR', logoutAtSql);
    this.apiV2Service.autologout(
      '/auth',
      'Your session has expired. Please login again.',
      'IDLE_3HR',
      logoutAtSql
    );
  }

  /** Local wall-clock → SQL datetime for V1 logout_at. */
  private formatSqlDateTime(ms: number): string {
    const d = new Date(ms);
    const pad = (n: number) => String(n).padStart(2, '0');
    return (
      `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ` +
      `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
    );
  }

  public cleanup() {
    this.loggingOut = false;
    this.warningActive = false;
    this.stayHandled = false;
    this.lastResetAt = 0;
    clearTimeout(this.warningTimer);
    clearTimeout(this.logoutTimer);
    clearInterval(this.countdownInterval);
    this.clearSessionHeartbeat();
    localStorage.removeItem(this.STORAGE_KEY);
    localStorage.removeItem(this.STAY_KEY);
    if (this.currentAlert) {
      this.currentAlert.dismiss().catch(() => {});
      this.currentAlert = null;
    }
  }

  /**
   * Call backend every 10 min (same idea as cron) so idle is enforced even if
   * local timers are throttled. Does NOT count as user activity.
   */
  private startSessionHeartbeat() {
    if (this.sessionCheckTimer || this.isOnExcludedRoute() || this.loggingOut) {
      return;
    }
    this.sessionCheckTimer = setInterval(
      () => this.pingSessionCheck(),
      this.SESSION_CHECK_MS
    );
  }

  private clearSessionHeartbeat() {
    if (this.sessionCheckTimer) {
      clearInterval(this.sessionCheckTimer);
      this.sessionCheckTimer = null;
    }
  }

  private pingSessionCheck() {
    if (this.isOnExcludedRoute() || this.loggingOut) {
      return;
    }
    if (!this.apiV2Service.currentUserValue?.v1_data?.token) {
      return;
    }
    // Also re-check wall clock locally (covers throttled background timers)
    this.handleReturnToApp();
    if (this.loggingOut) {
      return;
    }
    const api = this.apiV1;
    if (!api) {
      return;
    }
    api.checkSession().subscribe({
      next: () => {
        /* session still active — ApiAuth already validated idle */
      },
      error: () => {
        /* 401 idle → auth-refresh interceptor performs IDLE_3HR logout */
      },
    });
  }
}
